import { UIConstants } from '../../shared/models/Constants';
import {
  ApprovedAccessLite,
  evaluateCancelEligibility,
  evaluateEditEligibility,
  evaluateRemoveEligibility,
  evaluateRenewEligibility,
  isEditRenewSupportedTenant
} from './MyAccessRequests.lifecycleEligibility';
import { LifecycleGridItem } from './MyAccessRequests.types';

export type RowActionVisibility = {
  showEdit: boolean;
  showRenew: boolean;
  showCancel: boolean;
  showRemove: boolean;
  hasAnyAction: boolean;
};

export const getScopeFromAttributes = (attributes: any): string | undefined => {
  if (!attributes) {
    return undefined;
  }

  if (typeof attributes === 'string') {
    const raw = attributes.trim();
    return raw && raw.toLowerCase() !== 'na' ? raw : undefined;
  }

  if (Array.isArray(attributes)) {
    const parts = attributes
      .map((attr: any) => {
        const name = attr?.attributeName || attr?.name || attr?.key;
        const valueList = Array.isArray(attr?.attributeValues)
          ? attr.attributeValues
          : Array.isArray(attr?.values)
            ? attr.values
            : [];

        const values = valueList
          .map((value: any) => {
            if (typeof value === 'string') {
              return value;
            }

            if (value && typeof value === 'object') {
              if (value.description && value.code && value.description !== value.code) {
                return value.description;
              }

              return value.description || value.code || value.value || value.name;
            }

            return undefined;
          })
          .filter(Boolean)
          .join(', ');

        if (!values) {
          return undefined;
        }

        return name ? `${name}: ${values}` : values;
      })
      .filter(Boolean);

    return parts.length > 0 ? parts.join(' | ') : undefined;
  }

  if (typeof attributes === 'object') {
    const entries = Object.entries(attributes)
      .map(([key, value]) => {
        const values = Array.isArray(value)
          ? value.map((item) => `${item}`).filter(Boolean).join(', ')
          : `${value || ''}`;
        if (!values) {
          return undefined;
        }

        return `${key}: ${values}`;
      })
      .filter(Boolean);

    return entries.length > 0 ? entries.join(' | ') : undefined;
  }

  return undefined;
};

export const getRowActionVisibility = (
  item: LifecycleGridItem,
  allItems: LifecycleGridItem[],
  tenantName?: string,
  accessExpiryReminderWindowDays?: number,
  approvedAccesses: ApprovedAccessLite[] = []
): RowActionVisibility => {
  const context = {
    tenantName,
    allItems,
    accessExpiryReminderWindowDays,
    approvedAccesses
  };

  const showEdit = isEditRenewSupportedTenant(tenantName) && evaluateEditEligibility(item, context).isEligible;
  const showRenew = isEditRenewSupportedTenant(tenantName) && evaluateRenewEligibility(item, context).isEligible;
  const showCancel = evaluateCancelEligibility(item).isEligible;
  const showRemove = evaluateRemoveEligibility(item, { allItems, approvedAccesses }).isEligible;

  return {
    showEdit,
    showRenew,
    showCancel,
    showRemove,
    hasAnyAction: showEdit || showRenew || showCancel || showRemove
  };
};
